import React, { useRef } from 'react'
import cn from 'classnames';
import { Query } from 'react-apollo';

import GridFlaskController from '../../controllers/GridFlaskController';
import GridFlask from '../../components/GridFlask';
import PageContainer from '../../components/PageContainer';
import ActionsHeader from '../../components/ActionsHeader';
import BlockingStatus from '../../components/BlockingStatus';
import Col from '../../components/Col';
import { REP_OPTIONS as RepFilterOptions } from '../../components/actions/RepFilterAction';
import ActionsOrganizer from './components/ActionsOrganizerСoops';
import userSettingsQuery from '../../graphql/queries/userSettings.query';
import { getDefaultUserSettings, getUserSettings } from '../../utils'
import formatters from '../invoices/formatters'
import { isAccountRep } from '../../utils/isAccountRep';
import { MobileView, useMobileMediaQuery } from '../../config/mediaQuery';
import ModuleMenuBar from '../../components/ModuleMenuBar';

type Props = {
  isFullPage: boolean,
  showGroupPanel?: boolean,
  active: boolean,
};

const Coops = (props: Props) => {
  const moduleName = 'coops'

  const {
    isFullPage = true,
    showGroupPanel = false,
  } = props;

  const menuRef = useRef();

  const isMobile = useMobileMediaQuery();

  const modifyCoopsCollection = async ({ Main }) => {
    return Main.map(main => {
      return {
        ...main,
        Date: new Date(main.Date)
      }
    })
  }

  return (
    <PageContainer
      className={cn({
        'page--is-full-page': isFullPage,
        'page--is-nested': !isFullPage,
        'page--with-group-panel': showGroupPanel,
      })}
      isFullPage={isFullPage}
      active={props.active}
    >
      <Query
        query={userSettingsQuery}
        variables={{
          moduleName: moduleName,
        }}
      >
        {({ data: dataGrid, loading }: any) => {
          // we need to wait until the Query is loaded otherwise
          // we'll have to refetch the grid multiple times (first on
          // page load and then when this query is loaded)
          if (loading || !dataGrid)
            return (
              <BlockingStatus
                excludeHeader
                active
                container={document.body}
                text="Loading..."
              />
            );

          const userSettings = getUserSettings(dataGrid, {
            defaultFilters: settings => {
              if (settings && isAccountRep(settings.accountType)) {
                const [defaultOption] = RepFilterOptions;

                return {
                  Rep: {
                    // This could look to something like this:
                    //   Rep: {
                    //     AcctR: {
                    //       eq: 'CO01',
                    //     },
                    //   },
                    [defaultOption.value]: {
                      eq: settings.repCode,
                    },
                  },
                };
              }

              return {};
            },
          });

          const defaultUserSettings = getDefaultUserSettings(dataGrid)

          return (
            <GridFlaskController
              entity={moduleName}
              flattenFunction={modifyCoopsCollection}
              loading={loading}
              defaultSorting={defaultUserSettings.defaultSorting}
              defaultColumns={defaultUserSettings.defaultColumns}
              defaultGroupPanels={defaultUserSettings.defaultGroupPanels}
              {...userSettings}
            >
              {({
                collection,
                columnsMapping,
                currentItem,
                defaultColumns,
                filters,
                frozenColumns,
                grid,
                groups,
                hasAllColumnsHidden,
                onRowSelected,
                onGridInitialized,
                onGroupPanelChanged,
                onColSorted,
                onColResized,
                onColDragged,
                onColFreezed,
                onRowFreezed,
                onGroupCollapsedChanged,
                onClearSelection,
                onColumnsMappingChange,
                onViewSaved,
                onFiltersChanged,
                onInternalGridFiltersChanged,
                onCurrentItemChanged,
                onViewReset,
                onExporting,
                onExported,
                hasDataToDisplay,
                refetch,
                isLoading,
                status,
                saveStatus,
                selectedItems,
                userViewState,
                setFlexGridFilter,
                clearInternalFilters,
                toggleMultiSort,
                multiColumnSort
              }) => {
                return (
                  <React.Fragment>
                    <ActionsHeader
                      grid={grid}
                      collection={collection}
                      defaultColumnsMapping={defaultColumns}
                      columnsMapping={columnsMapping}
                      selectedItems={selectedItems}
                      moduleName={moduleName}
                      groups={groups}
                      saveStatus={saveStatus}
                      status={status}
                      loading={isLoading || status !== 'loaded'}
                      onViewSaved={onViewSaved}
                      onViewReset={onViewReset}
                      onColumnsMappingChange={onColumnsMappingChange}
                      onExporting={onExporting}
                      onExported={onExported}
                      userViewState={userViewState}
                      refetch={refetch}
                      hasAllColumnsHidden={hasAllColumnsHidden}
                      hasDataToDisplay={hasDataToDisplay}
                      frozenColumns={frozenColumns}
                      currentItem={currentItem}
                      toggleMultiSort={toggleMultiSort}
                      multiColumnSort={multiColumnSort}
                    >
                      {actions => {
                        const { isOneRowSelected, ColumnsAction } = actions;
                        return (
                          <>
                            <MobileView>
                              <ModuleMenuBar
                                moduleRefetch={refetch}
                                ColumnsAction={ColumnsAction}
                                clearInternalFilters={clearInternalFilters}
                              />
                            </MobileView>
                            <div
                              ref={menuRef}
                              style={{
                                justifyContent: 'space-between',
                                padding: '0 10px',
                                display: 'flex',
                                flexDirection: 'row',
                                minHeight: 'auto',
                                ...(isMobile && {
                                  position: 'absolute',
                                  bottom: 0,
                                  boxShadow:
                                    '0px -4px 4px 0px #ACACAC40',
                                  zIndex: 100,
                                  background: '#fff',
                                  borderRadius: '8px 8px 0px 0px',
                                  width: '100%',
                                  display: isOneRowSelected
                                    ? 'flex'
                                    : 'none',
                                  justifyContent: 'center',
                                  height: '90px'
                                }),
                              }}
                            >
                              <Col>
                                <ActionsOrganizer
                                  {...actions}
                                  minHeight="80px"
                                  onFiltersChanged={onFiltersChanged}
                                  filters={filters}
                                  isRep={isAccountRep(userSettings.accountType)}
                                  saveStatus={saveStatus}
                                  repCode={userSettings.repCode}
                                  onGroupCollapsedChanged={onGroupCollapsedChanged}
                                  moduleName={moduleName}
                                  clearInternalFilters={clearInternalFilters}
                                  displayRowMenu={isOneRowSelected}
                                  toggleMultiSort={toggleMultiSort}
                                />
                              </Col>
                            </div>
                          </>
                        )
                      }}
                    </ActionsHeader>
                    <div>
                      <GridFlask
                        collection={collection}
                        columnsMapping={columnsMapping}
                        id={moduleName}
                        grid={grid}
                        frozenColumns={frozenColumns}
                        hasAllColumnsHidden={hasAllColumnsHidden}
                        hasDataToDisplay={hasDataToDisplay}
                        onGridInitialized={onGridInitialized}
                        onInternalGridFiltersChanged={
                          onInternalGridFiltersChanged
                        }
                        onClearSelection={onClearSelection}
                        onRowSelected={onRowSelected}
                        onGroupPanelChanged={onGroupPanelChanged}
                        onColSorted={onColSorted}
                        onColResized={onColResized}
                        onColDragged={onColDragged}
                        onColFreezed={onColFreezed}
                        onRowFreezed={onRowFreezed}
                        status={status}
                        formatters={formatters}
                        showFrozenBars
                        showGroupPanel={false}
                        active={props.active}
                        groups={groups}
                        showTotalRow={false}
                        setFlexGridFilter={setFlexGridFilter}
                        onCurrentItemChanged={onCurrentItemChanged}
                        menuRef={menuRef}
                        isMobile={isMobile}
                        currentItem={currentItem}
                        multiColumnSort={multiColumnSort}
                      />
                    </div>
                  </React.Fragment>
                );
              }}
            </GridFlaskController>
          );
        }}
      </Query>
    </PageContainer>
  );
};

export default Coops;
